import {
  AdminDatePickerModal,
  AdminHeader,
  AdminSpinner,
  Box,
  Div,
  H5,
  ListSpinner,
  P,
  QueryStatus,
  SearchInput,
  TableHeader,
  handleSetUrlSearchParams,
  renderDateLabelWithToday,
  setTimeStartEnd,
  setTimeStartEndMonth,
} from '@vgrubs/components';
import debounce from 'lodash.debounce';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { NoDataComponent } from 'components/src/adminComponents/NoDataComponent/NoDataComponent';
import { AuditLogEntity } from 'components/src/types/auditLog.types';
import { UsersAndAccessDetailsModal } from '../UsersAndAccess/UsersAndAccessDetails';
import { useAuditLogInfiniteScroll } from './hooks/useAuditLogInfiniteScroll';

/** A log without a user was written by the system (automatic cron), not by a person. */
const renderLogUser = (user: AuditLogEntity['user']) =>
  user ? `${user.firstName} ${user.lastName}` : 'System';

export const AuditLog = () => {
  const today = new Date();

  const INITIAL_DATE = [
    setTimeStartEndMonth(today, 'start'),
    setTimeStartEndMonth(today, 'today'),
  ];

  const [userDetailsModal, setUserDetailsModal] = useState<
    AuditLogEntity['user'] | null
  >(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const initialQuery = searchParams.get('query') || '';
  const [inputValue, setInputValue] = useState(initialQuery);
  const [query, setQuery] = useState(initialQuery || '');
  const from = searchParams.get('from') ?? INITIAL_DATE[0];
  const to = searchParams.get('to') ?? INITIAL_DATE[1];

  const {
    logs: users,
    observerRef,
    isFetchingNextPage,
    isFetching,
    status,
  } = useAuditLogInfiniteScroll({ query, from, to });

  const debouncedSearch = useMemo(
    () => debounce((value: string) => setQuery(value), 500),
    []
  );

  const [dateInput, setDateInput] = useState<(Date | string)[]>(INITIAL_DATE);

  const setDate = (date: [string | null, string | null]) => {
    handleSetUrlSearchParams('from', date[0], setSearchParams);
    handleSetUrlSearchParams('to', date[1], setSearchParams);
  };

  const handleDateChange = (date: string[] | Date[]) => {
    setDateInput(date);
  };

  const handleDateApply = () => {
    if (dateInput.length < 2) {
      setDate([
        setTimeStartEnd(dateInput[0], 'start'),
        setTimeStartEnd(dateInput[0], 'end'),
      ]);
      setDateInput([dateInput[0], dateInput[0]] as Date[]);
    } else {
      setDate([
        setTimeStartEnd(dateInput[0], 'start'),
        setTimeStartEnd(dateInput[1], 'end'),
      ]);
    }
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    debouncedSearch(value);
    handleSetUrlSearchParams('query', value, setSearchParams);
  };

  return (
    <>
      <AdminHeader title={'Audit Log'}>
        <Div $flex $gap="20px" $gapSM="20px">
          <AdminDatePickerModal
            mode="range"
            value={dateInput}
            onChange={handleDateChange}
            click={handleDateApply}
          />
          <SearchInput
            value={inputValue}
            maxWidth="350px"
            maxWidthSM="130px"
            placeholder="Search"
            onChange={onSearchChange}
          />
        </Div>
      </AdminHeader>
      <Div>
        <H5 $m="32px 0" $mSM="20px 0">
          {renderDateLabelWithToday(from, to)}
        </H5>
      </Div>

      <TableHeader
        columns={[
          { label: 'DATE AND TIME', width: '20%' },
          { label: 'ACTION', width: '60%' },
          { label: 'USER', width: '20%' },
        ]}
      />

      {users.map((log) => {
        const formattedDate = DateTime.fromISO(log.createdAt)
          .setZone('local')
          .toFormat('MM/dd/yyyy - h:mm a');
        return (
          <div key={log.id}>
            <Box
              $mb="8"
              $flex
              $alignCenter
              $columnSM
              $gap="32px"
              $textAlignLeft
              $gapSM="16px"
              $desktop
            >
              <Div $width="20%" $widthSM="100%">
                <P $m="0">{formattedDate}</P>
              </Div>
              <Div $width="60%" $widthSM="100%">
                <P $m="0">{log.info} </P>
              </Div>
              <Div $width="20%" $widthSM="100%">
                <P
                  $pointer={!!log.user}
                  $m="0"
                  onClick={() => log.user && setUserDetailsModal(log.user)}
                  $hoverUnderline={!!log.user}
                >
                  {renderLogUser(log.user)}
                </P>
              </Div>
            </Box>

            <Box
              $mb="8"
              $flex
              $alignCenter
              $columnSM
              $gap="32px"
              $textAlignLeft
              $gapSM="16px"
              $mobile
            >
              <Div $width="20%" $widthSM="100%">
                <P $m="0">
                  <b>DATE AND TIME:</b> <br /> {formattedDate}
                </P>
              </Div>
              <Div $width="60%" $widthSM="100%">
                <P $m="0">
                  <b>ACTION:</b> <br /> {log.info}{' '}
                </P>
              </Div>
              <Div $width="20%" $widthSM="100%">
                <P
                  $pointer={!!log.user}
                  $m="0"
                  onClick={() => log.user && setUserDetailsModal(log.user)}
                  $hoverUnderline={!!log.user}
                >
                  <b>USER:</b> <br /> {renderLogUser(log.user)}
                </P>
              </Div>
            </Box>
          </div>
        );
      })}

      {users.length === 0 &&
        status !== QueryStatus.PENDING &&
        !isFetchingNextPage && (
          <NoDataComponent
            title="No Logs"
            description="No audit log entries found for the selected filters."
          />
        )}

      <ListSpinner loading={isFetchingNextPage} observerRef={observerRef} />
      {status === QueryStatus.PENDING && <AdminSpinner />}

      <UsersAndAccessDetailsModal
        modalIsOpen={userDetailsModal}
        closeModal={() => {
          setUserDetailsModal(null);
        }}
        noActions
        callGetUser
      />
    </>
  );
};
